import { checkName, Control211, ControlInlineEdit, ControlTypeAhead } from "aic";
import { useAppSettings, useTlfContext } from "contexts/AppSettingsContext";
import { getVariantPatchContext } from "contexts/tlf/getVariantPatchContext";
import {
  APPLY_VARIANT_TEMPLATE,
  REMOVE_VARIANT_TEMPLATE,
  RENAME_VARIANT_TEMPLATE,
  REVERT_TO_VARIANT_MASTER,
  UPSERT_VARIANT_TEMPLATE,
} from "contexts/tlf/interface";
import { eHierarchyLevel } from "functions/api/enums";
import { getDuplicateVariantName } from "functions/tlf/getDuplicateVariantName";
import { groupVariantPatches } from "functions/tlf/groupVariantPatches";
import { VariantTemplate } from "functions/tlf/isVariantTemplate";
import { KeyboardEvent, ReactElement, useState } from "react";
import { Alert, Button } from "react-bootstrap";
import { Check2, DashCircle, Pencil, XCircle } from "react-bootstrap-icons";
import { TlfVariantApplyButton } from "./TlfVariantApplyButton";

/**
 * Props for choosing, adding or removing a named variant.
 */
export interface TlfVariantNameSelectorProps {
  compositionValid: boolean;
  onSelectVariant: (name: string) => void;
  patchNames: string[];
  selectedName: string;
}

/**
 * Renders variant naming, inline description editing and ordered variant commands.
 *
 * @param props - Pending selection shared with the sidebar's patch controls
 * @param props.compositionValid - Whether the pending patches can be composed against the master
 * @param props.onSelectVariant - Updates the sidebar selection and clears its pending radio choices
 * @param props.patchNames - Pending patch choices owned by the sidebar
 * @param props.selectedName - Pending variant name chosen in the sidebar
 * @returns A labelled selector, editable description, ordered commands and variant warnings
 */
export const TlfVariantNameSelector = ({
  compositionValid,
  onSelectVariant,
  patchNames,
  selectedName,
}: TlfVariantNameSelectorProps): ReactElement | null => {
  const tlfContext = useTlfContext();
  const { hierarchyState } = useAppSettings();
  const { currentMaster } = getVariantPatchContext(tlfContext);
  const [namingMode, setNamingMode] = useState<"add" | "rename" | null>(null);
  const adding = namingMode === "add";
  const renaming = namingMode === "rename";
  const [newName, setNewName] = useState("");
  const [nameValidated, setNameValidated] = useState(false);
  if (tlfContext.readOnly && namingMode !== null) {
    setNamingMode(null);
  }
  if (currentMaster === null) return null;
  const design = hierarchyState.hierarchy?.level !== eHierarchyLevel.Report;
  const selectedTemplate = currentMaster.variantTemplates.find(
    (template) => template.name === selectedName,
  );
  const duplicateVariantName =
    !design || selectedTemplate === undefined
      ? undefined
      : getDuplicateVariantName(currentMaster.variantTemplates, patchNames, selectedTemplate.name);
  const resetToMaster =
    selectedTemplate === undefined && tlfContext.appliedVariantPatches.length > 0;
  const resetAvailable = resetToMaster;
  const createAvailable =
    resetToMaster &&
    getDuplicateVariantName(
      currentMaster.variantTemplates,
      tlfContext.appliedVariantPatches,
      "",
    ) === undefined;
  const selectionApplied =
    JSON.stringify([...patchNames].sort()) ===
    JSON.stringify([...tlfContext.appliedVariantPatches].sort());
  const action =
    selectedTemplate === undefined
      ? design
        ? createAvailable
          ? "create"
          : null
        : selectionApplied
          ? null
          : "apply"
      : selectionApplied
        ? null
        : "apply";
  const actionDisabled =
    tlfContext.readOnly ||
    !compositionValid ||
    duplicateVariantName !== undefined ||
    tlfContext.activeVariantPatchInvalidOps.length > 0;
  const compositionWarning =
    !compositionValid && groupVariantPatches(currentMaster).length === 0
      ? "Selected patches cannot be applied to this master"
      : undefined;
  const trimmedName = newName.trim();
  const nameInvalid =
    !nameValidated ||
    trimmedName.length === 0 ||
    currentMaster.variantTemplates.some(
      (template) =>
        (!renaming || template.name !== selectedTemplate?.name) &&
        template.name.toLowerCase() === trimmedName.toLowerCase(),
    );

  /**
   * Confirms a validated name through the owning sidebar.
   *
   * @param name - Candidate variant name from the input
   */
  const confirmNaming = async (name: string): Promise<void> => {
    const result = await checkName(
      "",
      {
        value: name,
        options: {
          existingValues: currentMaster.variantTemplates
            .filter((template) => !renaming || template.name !== selectedTemplate?.name)
            .map((template) => template.name),
        },
      },
      new AbortController(),
    );
    if (result.success && result.data.valid) {
      if (renaming && selectedTemplate !== undefined) {
        tlfContext.dispatch({
          operation: RENAME_VARIANT_TEMPLATE,
          variantName: selectedTemplate.name,
          newVariantName: name.trim(),
          calledFrom: "TlfVariantSidebar:renameVariant",
        });
      } else {
        tlfContext.dispatch({
          operation: UPSERT_VARIANT_TEMPLATE,
          template: { name: name.trim(), variantPatches: [...tlfContext.appliedVariantPatches] },
          calledFrom: "TlfVariantSidebar:addVariant",
        });
      }
      onSelectVariant(name.trim());
      setNamingMode(null);
      setNewName("");
      setNameValidated(false);
    }
  };

  /**
   * Cancels naming without changing the selected variant.
   */
  const cancelNaming = (): void => {
    setNamingMode(null);
    setNewName("");
    setNameValidated(false);
  };

  /**
   * Activates an unboxed icon from the keyboard.
   *
   * @param event - Keyboard event received by the icon
   * @param action - Command run for Enter or Space
   */
  const activateIcon = (event: KeyboardEvent<SVGElement>, action: () => void): void => {
    if (["Enter", " "].includes(event.key)) {
      event.preventDefault();
      action();
    }
  };

  /**
   * Starts renaming a selected definition with its current name as the draft.
   *
   * @param template - Selected definition to prefill in the rename control
   */
  const beginRename = (template: VariantTemplate): void => {
    setNewName(template.name);
    setNameValidated(false);
    setNamingMode("rename");
  };

  /**
   * Removes a definition and notifies the sidebar of the next remaining selection.
   *
   * @param variantName - Selected definition to remove from the current master
   */
  const removeVariant = (variantName: string): void => {
    tlfContext.dispatch({
      operation: REMOVE_VARIANT_TEMPLATE,
      variantName,
      calledFrom: "TlfVariantSidebar:removeVariant",
    });
    onSelectVariant(
      currentMaster.variantTemplates.find((template) => template.name !== variantName)?.name ?? "",
    );
  };

  /**
   * Applies the pending composition or restores the master for an unnamed selection.
   *
   * @param revertToMaster - Whether to restore the master instead of applying the pending patches
   */
  const applySelection = (revertToMaster: boolean): void => {
    if (revertToMaster) {
      tlfContext.dispatch({
        operation: REVERT_TO_VARIANT_MASTER,
        calledFrom: "TlfVariantSidebar:apply",
      });
    } else {
      tlfContext.dispatch({
        operation: APPLY_VARIANT_TEMPLATE,
        patchNames,
        calledFrom: "TlfVariantSidebar:apply",
      });
    }
  };

  return (
    <div>
      <div className="d-flex align-items-start gap-2">
        <div
          className="flex-grow-1"
          style={{ minWidth: 0 }}
        >
          {namingMode !== null ? (
            <ControlTypeAhead
              id="tlf-new-variant-name"
              label="Variant name"
              labelWidth={3}
              value={newName}
              setValue={setNewName}
              availableList={currentMaster.variantTemplates
                .filter((template) => !renaming || template.name !== selectedTemplate?.name)
                .map((template) => ({ value: template.name }))}
              validCondition={checkName}
              validationReturn={setNameValidated}
              onEnter={(name) => void confirmNaming(name)}
              onEscape={cancelNaming}
            />
          ) : (
            <Control211
              id="tlf-variant-name"
              label="Variant"
              labelWidth={3}
              value={selectedTemplate?.name ?? ""}
              setValue={onSelectVariant}
              options={[
                { value: "", label: "No variant" },
                ...currentMaster.variantTemplates.map((template) => ({
                  value: template.name,
                  label: template.name,
                })),
              ]}
            />
          )}
        </div>
        {!tlfContext.readOnly && (design || action !== null || resetAvailable) && (
          <div
            className="d-flex align-items-center gap-2 mt-1 flex-shrink-0"
            style={{ height: "calc(1.8125rem + 2px)" }}
          >
            {namingMode === null && (
              <>
                {action === "apply" && (
                  <TlfVariantApplyButton
                    action="apply"
                    disabled={actionDisabled}
                    onApply={() => applySelection(false)}
                  />
                )}
                {resetAvailable && (
                  <TlfVariantApplyButton
                    action="reset"
                    disabled={actionDisabled}
                    onApply={() => applySelection(true)}
                  />
                )}
                {design && selectedTemplate !== undefined && (
                  <>
                    <Button
                      id="tlf-rename-variant"
                      variant="link"
                      className="p-0 text-primary flex-shrink-0"
                      disabled={tlfContext.readOnly}
                      aria-label="Rename variant"
                      title="Rename variant"
                      onClick={() => beginRename(selectedTemplate)}
                    >
                      <Pencil size={16} />
                    </Button>
                    <Button
                      id="tlf-remove-variant"
                      variant="link"
                      className="p-0 text-danger flex-shrink-0"
                      disabled={tlfContext.readOnly}
                      aria-label="Remove variant"
                      title="Remove variant"
                      onClick={() => removeVariant(selectedTemplate.name)}
                    >
                      <DashCircle size={16} />
                    </Button>
                  </>
                )}
                {action === "create" && (
                  <TlfVariantApplyButton
                    action="create"
                    disabled={actionDisabled}
                    onApply={() => {
                      setNewName("");
                      setNameValidated(false);
                      setNamingMode("add");
                    }}
                  />
                )}
              </>
            )}
            {namingMode !== null && (
              <>
                <Check2
                  id="tlf-add-variant-confirm"
                  role="button"
                  tabIndex={nameInvalid ? -1 : 0}
                  aria-disabled={nameInvalid}
                  size={16}
                  className={`${nameInvalid ? "text-muted" : "text-primary"} flex-shrink-0`}
                  style={{ cursor: nameInvalid ? "not-allowed" : "pointer" }}
                  aria-label="Confirm variant"
                  title="Confirm variant"
                  onClick={() => void confirmNaming(newName)}
                  onKeyDown={(event) => activateIcon(event, () => void confirmNaming(newName))}
                />
                <XCircle
                  id="tlf-add-variant-cancel"
                  role="button"
                  tabIndex={0}
                  size={16}
                  className="text-primary flex-shrink-0"
                  style={{ cursor: "pointer" }}
                  aria-label="Cancel"
                  title="Cancel"
                  onClick={cancelNaming}
                  onKeyDown={(event) => activateIcon(event, cancelNaming)}
                />
              </>
            )}
          </div>
        )}
      </div>
      {selectedTemplate !== undefined && (
        <div className="mt-2">
          <ControlInlineEdit
            key={selectedTemplate.name}
            id="tlf-variant-description"
            aria-label="Variant description"
            placeholder="Variant description"
            value={selectedTemplate.description ?? ""}
            disabled={tlfContext.readOnly || !design}
            editLabel="Edit description"
            type="textarea"
            rows={2}
            allowCarriageReturn
            setValue={
              tlfContext.readOnly
                ? undefined
                : (description) =>
                    tlfContext.dispatch({
                      operation: UPSERT_VARIANT_TEMPLATE,
                      template: { ...selectedTemplate, description },
                      calledFrom: "TlfVariantNameSelector:editDescription",
                    })
            }
          />
        </div>
      )}
      {!adding && !renaming && duplicateVariantName !== undefined && (
        <Alert
          variant="warning"
          className="mt-2 mb-0"
        >
          This variant has the same patches as {duplicateVariantName}
        </Alert>
      )}
      {compositionWarning !== undefined && (
        <Alert
          variant="danger"
          className="mt-2 mb-0"
        >
          {compositionWarning}
        </Alert>
      )}
    </div>
  );
};

TlfVariantNameSelector.displayName = "TlfVariantNameSelector";
